"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function JoinTheMva() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="34987" id="lead-capture-form" className="bg-background py-32 min-h-screen flex items-center justify-center">
      <div ref={ref} className={`container max-w-2xl mx-auto px-4 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <h2 className="text-4xl md:text-5xl font-serif font-medium tracking-tight text-center mb-4 text-foreground leading-tight">
          Lead Capture Form
        </h2>
        <p className="text-center text-muted-foreground font-sans text-lg mb-12 max-w-lg mx-auto">
          Complete the form below to receive your personalized health assessment and expert treatment recommendations.
        </p>

        <div className="bg-card p-8 md:p-12 rounded-2xl shadow-sm border border-border/50">
          <FormProvider formKey="lead_capture_form_freeonlinepostureassessment" formId="lead_capture_form_freeonlinepostureassessment" sectionName="lead-capture-form" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          city: "",
          consent: false
        }} validationRules={{
          full_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          consent: {
            required: true
          }
        }} submitText="Get My Free Assessment" successMessage="Thank you! Your assessment request has been received. We'll send your results shortly." errorMessage="There was a problem processing your request. Please try again.">
            <PostureAssessmentForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function PostureAssessmentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="w-16 h-16 bg-accent/10 text-accent rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-serif font-semibold mb-4 text-foreground">Assessment Request Received</h3>
        <p className="text-lg mb-8 text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} className="h-14 px-10 text-lg font-medium rounded-full bg-accent text-accent-foreground hover:bg-accent/90 transition-all w-full md:w-auto shadow-md">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground font-semibold">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" {...field} className="h-12 px-0 text-lg border-0 border-b-2 border-muted-foreground/30 rounded-none shadow-none focus-visible:ring-0 focus-visible:border-accent bg-transparent transition-colors" />
            </FormControl>
            <FormDescription className="font-sans text-[10px] text-muted-foreground/70">
              Enter your first and last name.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground font-semibold">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" {...field} className="h-12 px-0 text-lg border-0 border-b-2 border-muted-foreground/30 rounded-none shadow-none focus-visible:ring-0 focus-visible:border-accent bg-transparent transition-colors" />
            </FormControl>
            <FormDescription className="font-sans text-[10px] text-muted-foreground/70">
              We'll send your posture assessment results to this email.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground font-semibold">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+357 99 123456" {...field} className="h-12 px-0 text-lg border-0 border-b-2 border-muted-foreground/30 rounded-none shadow-none focus-visible:ring-0 focus-visible:border-accent bg-transparent transition-colors" />
              </FormControl>
              <FormDescription className="font-sans text-[10px] text-muted-foreground/70">
                Optional – in case we need to contact you.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="city" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground font-semibold">
                City
              </FormLabel>
              <FormControl>
                <Input placeholder="Limassol or Nicosia" {...field} className="h-12 px-0 text-lg border-0 border-b-2 border-muted-foreground/30 rounded-none shadow-none focus-visible:ring-0 focus-visible:border-accent bg-transparent transition-colors" />
              </FormControl>
              <FormDescription className="font-sans text-[10px] text-muted-foreground/70">
                Your current city (helps us suggest the nearest clinic).
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="consent" rules={{
      required: "You must agree to receive communications"
    }} render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-4 rounded-xl border border-border bg-muted/30 p-4">
            <FormControl>
              <input type="checkbox" checked={value} onChange={onChange} {...field} className="h-5 w-5 border-2 border-muted-foreground/30 rounded-sm text-accent focus:ring-accent focus:ring-offset-0 cursor-pointer mt-1" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="font-sans text-sm font-medium text-foreground/90 cursor-pointer">
                I agree to receive communications from Bodywerks
              </FormLabel>
              <FormDescription className="font-sans text-[10px] text-muted-foreground/70">
                We respect your privacy and will only send relevant updates.
              </FormDescription>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-lg bg-destructive/10 text-destructive text-sm font-medium animate-in fade-in">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-16 text-sm font-sans font-bold uppercase tracking-widest rounded-full bg-accent text-accent-foreground hover:bg-accent/90 hover:scale-[1.01] active:scale-[0.99] transition-all shadow-lg mt-8">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Processing Assessment...
          </> : "Get My Free Assessment"}
      </Button>
    </Form>;
}